import React, { useState } from "react";
import { NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LayoutDashboard, ShoppingCart, Package, Truck, Tag, BarChart3, Settings, LogOut, Menu, X, Loader2 } from "lucide-react";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/orders", label: "Orders", icon: ShoppingCart },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/delivery", label: "Delivery Settings", icon: Truck },
  { to: "/admin/promos", label: "Promo Codes", icon: Tag },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/settings", label: "Store Settings", icon: Settings },
];

export function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  if (user === null) return <div className="min-h-screen bg-obsidian flex items-center justify-center"><Loader2 className="animate-spin text-gold" size={40} /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const doLogout = () => { logout(); nav("/admin/login"); };

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-gold/20 flex justify-center"><Logo /></div>
      <nav className="flex-1 p-3 space-y-1">
        {LINKS.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${isActive ? "bg-gold/15 text-gold" : "text-cream/70 hover:bg-white/5 hover:text-cream"}`}
            data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <l.icon size={18} /> {l.label}
          </NavLink>
        ))}
      </nav>
      <button onClick={doLogout} className="m-3 flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-cream/70 hover:bg-destructive/15 hover:text-destructive" data-testid="admin-logout-btn">
        <LogOut size={18} /> Logout
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-obsidian flex">
      <aside className="hidden lg:block w-64 border-r border-gold/20 bg-card-surface/40 sticky top-0 h-screen">
        <Sidebar />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card-surface border-r border-gold/20 h-full"><Sidebar /></div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between p-4 bg-obsidian/90 backdrop-blur border-b border-gold/20">
          <button onClick={() => setOpen(!open)} className="text-cream" data-testid="admin-mobile-menu">{open ? <X /> : <Menu />}</button>
          <Logo />
          <div className="w-6" />
        </div>
        <div className="p-4 sm:p-6 lg:p-8">{children}</div>
      </div>
    </div>
  );
}
